import { PRODUCT_TYPE, type ProductType } from "@/constants";
import {
  FilteredBeneficiaryDetail,
  type FilteredPricingEngineRequest,
  FilteredResolvedBlockchainBeneficiary,
  type PricingSwapInstruction,
  TenorType,
} from "@generated/types";

/**
 * Context required to build a swap instruction — everything the
 * pure function needs that previously lived in PricingProvider's closure.
 */
export interface SwapInstructionContext {
  exchangeState: {
    sellCurrency: string;
    sellAmount: number;
    buyCurrency: string;
    buyAmount: number;
    lastEditedSide: "sell" | "buy";
    quoteType: string;
  };
  sourceData: FilteredPricingEngineRequest | null | undefined;
  remittanceInformation: string | undefined;
  purposeCode: string | undefined;
  beneficiary:
    | FilteredBeneficiaryDetail
    | FilteredResolvedBlockchainBeneficiary
    | undefined;
  type: ProductType | undefined;
  isPaymentFlow: boolean;
}

/**
 * Builds a PricingSwapInstruction from the current exchange state and context.
 *
 * This is a pricing-engine REQUEST body, so it is PricingSwapInstruction (which
 * carries the required marginCurrency), not the FilteredSwapInstruction response
 * schema. Typing it as the response shape is what forced the double cast below
 * and hid marginCurrency from the type checker.
 *
 * Pure function — no hooks, no side effects, no closures.
 * Extracted from PricingProvider to enable unit testing and reduce provider size.
 */
export function createSwapInstruction(
  ctx: SwapInstructionContext,
  partialSwapInstruction?: Partial<PricingSwapInstruction>,
  overrideRemittanceInformation?: string,
  overridePurposeCode?: string
): PricingSwapInstruction | undefined {
  const { exchangeState, sourceData, beneficiary, type, isPaymentFlow } = ctx;

  // Resolve currencies with a fallback to the request's own instruction.
  // On the review step the requote can fire before `exchangeState` has synced
  // its currencies from the fetched request (the /pay review URL carries no
  // ?sell/?buy), leaving sellCurrency = "". That blank flows into
  // `marginCurrency`, and the pricing engine rejects the Requote with a 400
  // Bean-Validation error — `marginCurrency: must match "^[A-Z]{3,16}$"`
  // (CUSTOMER-BFF-J). The request instruction is the authoritative pair on the
  // review step, so fall back to it.
  const sourceInstruction = sourceData?.instructions?.[0];
  const resolvedSellCurrency =
    exchangeState.sellCurrency || sourceInstruction?.payCurrency || "";
  const resolvedBuyCurrency =
    exchangeState.buyCurrency || sourceInstruction?.receiveCurrency || "";

  // Safety check: never build an instruction with an empty receive currency.
  // The engine validates `^[A-Z]{3,16}$` on `updates[0].receiveCurrency`, so an
  // empty value is a guaranteed 400 (CUSTOMER-UI-2D / CUSTOMER-BFF-J).
  //
  // This guard used to be payment-flow only, which left the exchange flow free
  // to POST `receiveCurrency: ""`. That is exactly what happens when a currency
  // drops out of the customer's capabilities mid-session (2026-08-18: AED
  // desynced upstream): every currency resolver in PricingProvider resolves
  // against `capabilities.tradable`, so `buyCurrency` collapses to "" and the
  // "Refresh rate" button 400s on every click while the displayed rate stays
  // stale. Callers surface the reason instead — see `resolveUnavailableCurrency`
  // and `exchange-review.tsx`.
  if (!resolvedBuyCurrency) {
    console.error(
      isPaymentFlow
        ? "Cannot create swap instruction: receiveCurrency is empty in payment flow"
        : "Cannot create swap instruction: receiveCurrency is empty"
    );
    return undefined;
  }

  // Safety check: never build an instruction with an empty sell/margin
  // currency — it fails the engine's `^[A-Z]{3,16}$` validation and 400s.
  // Returning undefined lets the caller surface feedback instead of firing a
  // request that is guaranteed to fail (CUSTOMER-BFF-J).
  if (!resolvedSellCurrency) {
    console.error(
      "Cannot create swap instruction: sell/margin currency is empty"
    );
    return undefined;
  }

  // Use override values if provided, otherwise fall back to context
  const effectiveRemittanceInfo =
    overrideRemittanceInformation ?? ctx.remittanceInformation;
  const effectivePurposeCode = overridePurposeCode ?? ctx.purposeCode;

  return {
    ...sourceInstruction,
    payAmount:
      exchangeState.lastEditedSide === "sell"
        ? exchangeState.sellAmount
        : undefined,
    receiveAmount:
      exchangeState.lastEditedSide === "buy"
        ? exchangeState.buyAmount
        : undefined,
    quote: exchangeState.quoteType,
    tenor: TenorType.Instant,
    ...partialSwapInstruction,
    // Currency fields are asserted AFTER the partial spread. Callers
    // (getPricingQuote / getMaxAmountQuote) pass a partial whose
    // payCurrency/receiveCurrency come straight from the live exchangeState —
    // which is "" on the pre-sync review step (the CUSTOMER-BFF-J root cause).
    // Spreading the partial last would re-clobber the resolved values back to
    // "". The partial's currencies derive from the SAME exchangeState as the
    // resolved values, so re-asserting never overrides a legitimate currency —
    // it only fills the empty case. marginCurrency must track payCurrency (the
    // engine validates `^[A-Z]{3,16}$`).
    payCurrency: resolvedSellCurrency,
    receiveCurrency: resolvedBuyCurrency,
    marginCurrency: resolvedSellCurrency,
    // FIAT delivery instruction
    ...(beneficiary &&
      type === PRODUCT_TYPE.CURRENCIES && {
        deliveryInstruction: {
          beneficiary: (beneficiary as FilteredBeneficiaryDetail).id,
          operationCode: (beneficiary as FilteredBeneficiaryDetail).defaults
            ?.operationCode,
          chargeCode: (beneficiary as FilteredBeneficiaryDetail).defaults
            ?.chargeCode,
          // Sent as typed, "" included: on a requote the BFF keeps the stored
          // value for an omitted field and clears it for "". A falsy spread
          // here would turn a reference the user emptied back into the stored
          // one, because it would omit the "" and the BFF would keep the
          // stored value for the omitted field.
          // Not known yet (`undefined`, or a `null` restored from the
          // session snapshot) omits the key, which keeps the stored value.
          ...(effectiveRemittanceInfo != null && {
            remittanceInformation: effectiveRemittanceInfo,
          }),
          ...(effectivePurposeCode != null && {
            purposeCode: effectivePurposeCode,
            paymentReason: effectivePurposeCode,
          }),
        },
      }),
    // BLOCKCHAIN delivery instruction
    ...(beneficiary &&
      type === PRODUCT_TYPE.DIGITAL_ASSETS && {
        deliveryInstruction: {
          beneficiary: (beneficiary as FilteredResolvedBlockchainBeneficiary)
            .id,
        },
      }),
  } as PricingSwapInstruction;
}

/**
 * Pure decision for the PricingProvider "empty receive currency" watchdog.
 *
 * `buildSwapInstruction` reports a Sentry silent-failure when
 * `createSwapInstruction` returns undefined in a payment flow. But a payment
 * flow on `/pay/currencies/{beneficiaryId}` legitimately produces an undefined
 * instruction during the PRE-RESOLUTION entry window — the user has typed a
 * sell amount but the receive currency hasn't resolved/been selected yet. That
 * is the expected transient state, not a stuck flow, and reporting it produced
 * false positives (CUSTOMER-UI-6N).
 *
 * Only report once currencies are actually resolved (a genuine stuck state).
 * Extracted as a pure predicate so the gate is unit-testable without rendering
 * the provider (which needs Redux + RTK Query + nuqs + beneficiary hooks).
 */
export function shouldReportEmptyReceiveCurrency(args: {
  swapInstruction: PricingSwapInstruction | undefined;
  isPaymentFlow: boolean;
  areCurrenciesResolved: boolean;
}): boolean {
  return (
    !args.swapInstruction && args.isPaymentFlow && args.areCurrenciesResolved
  );
}
